import type { ApartmentAverageForProject } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import { measureUnit, serviceElement } from '@obraplan/core/domain/takeoff/applies-to';
import type {
  PieceElement,
  StructurePieceElement,
} from '@obraplan/core/domain/takeoff/foundation-factors';
import {
  ownFactorOf,
  type ItemFactor,
  type RoomKindAverage,
} from '@obraplan/core/domain/takeoff/own-factor';
import { pileRateKey, type PileType } from '@obraplan/core/domain/takeoff/pile';
import type { PileCapType } from '@obraplan/core/domain/takeoff/pile-cap-type';
import type { StructuralElementType } from '@obraplan/core/domain/takeoff/element-type';
import type { StructuralElementKind } from '@obraplan/core/domain/takeoff/structural-element';
import { descendsToRooms } from '@obraplan/core/domain/takeoff/room-quantity';
import {
  readsSteelRate,
  STEEL_GAUGES,
  type SteelGauge,
  type SteelRate,
} from '@obraplan/core/domain/takeoff/steel';
import type {
  CatalogItem,
  CatalogService,
  ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import { aprovacaoDe, type AprovacaoDoServico, type ServicosAprovados } from './servicos-aprovados';
import { mediaDaBitola, mediaDoItem } from './trazer-a-media-do-apartamento';
import { listaDaTaxa, temListaPropria } from './taxa-por-elemento';

/**
 * A lista de fatores do card da estaca e do cômodo (Fase 39, etapa 3 — RN-511 a RN-513).
 *
 * Cada linha é um item de um serviço que vale ali — o que mede a estaca, na estaca; o que desce a
 * cômodo, no cômodo —, com o fator que vale sem o próprio ao lado: o do tipo, ou o índice da obra.
 * Conta pura, fora do componente, para a tela e o teste lerem a mesma lista.
 *
 * O item de aço fica de fora: o aço da estaca reparte pelas bitolas do tipo (RN-378), e não tem
 * fator a mudar por peça.
 */

/** De onde vem o fator que vale sem o próprio. */
export type OrigemDaBase = 'TIPO' | 'OBRA';

/**
 * A média de hoje das obras marcadas, ao lado do número do card (Fase 41, RN-554 — F6). Só para
 * ler: o tipo de bloco e o próprio do card não mudam com ela (D8).
 */
export interface MediaAoLado {
  readonly rate: number;
  /** De quantas obras ela veio. */
  readonly projects: number;
}

export interface LinhaDeFator {
  readonly serviceKey: string;
  readonly servico: string;
  readonly itemKey: string;
  readonly item: string;
  /** "M³ por m³" — a unidade do item por unidade da medida do serviço. */
  readonly unidade: string;
  /** O fator que vale sem o próprio. `undefined`: o item não calcula ali sem um. */
  readonly base: number | undefined;
  readonly origemDaBase: OrigemDaBase | undefined;
  /**
   * A base é a linha do tipo que segue a média (Fase 42, etapa 4 — RN-582; ADR-191): o tipo nasceu
   * com ela, ou o "Atualizar" a trouxe. O card diz "do tipo, da média".
   */
  readonly baseDaMedia?: true;
  /** O fator próprio da peça ou do cômodo, quando há (RN-513). */
  readonly proprio: number | undefined;
  /** O próprio que veio da média do tipo de cômodo (Fase 40, RN-527), e de qual tipo. */
  readonly daMediaDoTipo?: RoomKindAverage;
  /**
   * A média do serviço × item, ao lado: a da fundação no bloco, no baldrame e no radier (Fase 41,
   * RN-554); a da estrutura no pilar, na viga, na escada e na contenção (Fase 42, RN-584).
   */
  readonly mediaAoLado?: MediaAoLado;
}

/** Um serviço do card, com as linhas dele — a tela agrupa por serviço. */
export interface ServicoDoCard {
  readonly serviceKey: string;
  readonly servico: string;
  readonly linhas: readonly LinhaDeFator[];
}

function unidadeDoFator(servico: CatalogService, item: CatalogItem): string {
  const daMedida = measureUnit(servico.appliesTo, servico.measureBy);
  const doItem = item.unit ?? 'un';
  return daMedida === '' ? doItem : `${doItem} por ${daMedida}`;
}

function indiceDaObra(item: CatalogItem): number | undefined {
  return item.consumptionIndex !== undefined && item.consumptionIndex > 0
    ? item.consumptionIndex
    : undefined;
}

/** A média de hoje do item, pelo par dele na referência — a mesma que a aba dos itens mostra. */
function mediaAoLadoDe(
  servico: CatalogService,
  item: CatalogItem,
  media: ApartmentAverageForProject | undefined,
): { readonly mediaAoLado?: MediaAoLado } {
  if (media === undefined) return {};
  const hoje = mediaDoItem(item, servico, media);
  return hoje === undefined ? {} : { mediaAoLado: { rate: hoje.rate, projects: hoje.projects } };
}

function montar(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  valeAli: (servico: CatalogService) => boolean,
  base: (
    servico: CatalogService,
    item: CatalogItem,
  ) => Pick<LinhaDeFator, 'base' | 'origemDaBase' | 'baseDaMedia'>,
  factors: readonly ItemFactor[] | undefined,
  media?: ApartmentAverageForProject,
): readonly ServicoDoCard[] {
  if (catalog === undefined) return [];
  return catalog.disciplines.flatMap((disciplina) =>
    disciplina.services.flatMap((servico): ServicoDoCard[] => {
      if (!valeAli(servico)) return [];
      const linhas = servico.items
        .filter((item) => item.steelGauge === undefined)
        .map((item): LinhaDeFator => ({
          serviceKey: servico.key,
          servico: servico.name,
          itemKey: item.key,
          item: item.description,
          unidade: unidadeDoFator(servico, item),
          ...base(servico, item),
          proprio: ownFactorOf(factors, servico.key, item.key),
          ...mediaDoTipo(factors, servico.key, item.key),
          ...mediaAoLadoDe(servico, item, media),
        }));
      return linhas.length === 0
        ? []
        : [{ serviceKey: servico.key, servico: servico.name, linhas }];
    }),
  );
}

/**
 * Os itens dos serviços que medem a estaca, com o fator do tipo — ou, sem ele, o índice da obra,
 * como a conta faz (RN-508) — e o próprio da estaca (RN-511).
 */
export function fatoresDaEstaca(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  estaca: { readonly factors?: readonly ItemFactor[] },
  tipo: Pick<PileType, 'rates'> | undefined,
): readonly ServicoDoCard[] {
  const doTipo = new Map(
    (tipo?.rates ?? []).map((linha) => [pileRateKey(linha.serviceKey, linha.itemKey), linha.rate]),
  );
  return montar(
    catalog,
    (servico) => serviceElement(servico) === 'ESTACA',
    (servico, item) => {
      const fator = doTipo.get(pileRateKey(servico.key, item.key));
      if (fator !== undefined) return { base: fator, origemDaBase: 'TIPO' };
      const indice = indiceDaObra(item);
      return indice === undefined
        ? { base: undefined, origemDaBase: undefined }
        : { base: indice, origemDaBase: 'OBRA' };
    },
    estaca.factors,
  );
}

/** O índice da obra como base da linha — o que vale na peça sem fator nenhum. */
function baseDaObra(
  _servico: CatalogService,
  item: CatalogItem,
): Pick<LinhaDeFator, 'base' | 'origemDaBase'> {
  const indice = indiceDaObra(item);
  return indice === undefined
    ? { base: undefined, origemDaBase: undefined }
    : { base: indice, origemDaBase: 'OBRA' };
}

/**
 * Os itens dos serviços que medem o bloco, com o fator do tipo de bloco — ou, sem ele, o índice
 * da obra — e o próprio do bloco (Fase 41, RN-538; ADR-185). A mesma ordem da conta: o próprio,
 * o do tipo, o índice.
 *
 * Com a média da fundação (`media`), cada linha leva a média de hoje ao lado (RN-554): o tipo e o
 * próprio ficam digitados por cima dela (F6).
 */
export function fatoresDoBloco(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  bloco: { readonly factors?: readonly ItemFactor[] },
  tipo: Pick<PileCapType, 'rates'> | undefined,
  media?: ApartmentAverageForProject,
): readonly ServicoDoCard[] {
  return fatoresComTipo(catalog, 'BLOCO', bloco.factors, tipo?.rates, media);
}

/**
 * Os itens dos serviços que medem o pilar ou a viga, com o fator do tipo deles — ou, sem ele, o
 * índice da obra — e o próprio da peça (Fase 42, RN-561, RN-563). A ordem da conta, a do bloco:
 * o próprio, o do tipo, o índice. O tipo de outro elemento não vale (RN-257) — quem chama passa o
 * do mesmo elemento.
 *
 * Com a média da estrutura (`media`, etapa 4 — RN-584), cada linha leva a de hoje ao lado, como no
 * bloco (RN-554).
 */
export function fatoresDoElemento(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  elemento: { readonly kind: StructuralElementKind; readonly factors?: readonly ItemFactor[] },
  tipo: Pick<StructuralElementType, 'rates'> | undefined,
  media?: ApartmentAverageForProject,
): readonly ServicoDoCard[] {
  return fatoresComTipo(catalog, elemento.kind, elemento.factors, tipo?.rates, media);
}

/**
 * A peça que tem tipo: o fator do tipo é a base da linha; sem ele, o índice da obra. A linha do
 * tipo que segue a média diz que é da média (Fase 42, etapa 4 — ADR-191).
 */
function fatoresComTipo(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  elemento: 'BLOCO' | StructuralElementKind,
  factors: readonly ItemFactor[] | undefined,
  doTipo: readonly ItemFactor[] | undefined,
  media?: ApartmentAverageForProject,
): readonly ServicoDoCard[] {
  return montar(
    catalog,
    (servico) => serviceElement(servico) === elemento,
    (servico, item) => {
      const linha = doTipo?.find(
        (candidata) => candidata.serviceKey === servico.key && candidata.itemKey === item.key,
      );
      if (linha === undefined) return baseDaObra(servico, item);
      return linha.origin === 'MEDIA'
        ? { base: linha.factor, origemDaBase: 'TIPO', baseDaMedia: true }
        : { base: linha.factor, origemDaBase: 'TIPO' };
    },
    factors,
    media,
  );
}

/**
 * Os itens dos serviços que medem o baldrame ou o radier, com o índice da obra ao lado e o
 * próprio da peça (Fase 41, RN-538 — F7: sem tipo). Com a média da fundação, ela também (RN-554).
 * A escada, o muro e o pilar de contenção pela mesma lista (Fase 42, etapa 3 — G3, RN-571): sem
 * tipo, a base é sempre o índice da obra; com a média da estrutura, ela ao lado (etapa 4, RN-584).
 */
export function fatoresDaPecaDaFundacao(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  elemento: 'BALDRAME' | 'RADIER' | StructurePieceElement,
  peca: { readonly factors?: readonly ItemFactor[] },
  media?: ApartmentAverageForProject,
): readonly ServicoDoCard[] {
  return montar(
    catalog,
    (servico) => serviceElement(servico) === elemento,
    baseDaObra,
    peca.factors,
    media,
  );
}

/**
 * Os itens dos serviços que descem a cômodo (`descendsToRooms`), com o índice da obra ao lado e o
 * próprio do cômodo (RN-512).
 */
export function fatoresDoComodo(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  comodo: { readonly factors?: readonly ItemFactor[] },
): readonly ServicoDoCard[] {
  return montar(catalog, descendsToRooms, baseDaObra, comodo.factors);
}

/** A foto da média do tipo, quando o próprio veio dela (Fase 40, RN-527). */
function mediaDoTipo(
  factors: readonly ItemFactor[] | undefined,
  serviceKey: string,
  itemKey: string,
): { readonly daMediaDoTipo?: RoomKindAverage } {
  const linha = factors?.find((f) => f.serviceKey === serviceKey && f.itemKey === itemKey);
  return linha?.origin === 'MEDIA' && linha.average !== undefined
    ? { daMediaDoTipo: linha.average }
    : {};
}

/** Quantos itens têm fator próprio — o número do título do card. */
export function quantosProprios(servicos: readonly ServicoDoCard[]): number {
  return servicos.reduce(
    (soma, servico) => soma + servico.linhas.filter((linha) => linha.proprio !== undefined).length,
    0,
  );
}

/** `1.05` vira `"1,05"`, e `0.019712` vira `"0,019712"` — o fator com as casas que tiver. */
export function textoDoFator(valor: number): string {
  return valor.toLocaleString('pt-BR', { maximumFractionDigits: 6 });
}

// ── A taxa de aço da peça (Fase 41, etapa 2; Fase 42, etapa 2) ──────────────────

/** De onde vem a taxa que a peça segue sem a própria (RN-544, RN-568, RN-572). */
export interface TaxaDeBase {
  /** A lista que vale sem a própria; `undefined` quando nem o tipo nem a disciplina têm. */
  readonly taxas: readonly SteelRate[] | undefined;
  /** O tipo que dá a taxa — o "Aplicar ao tipo" e o "Voltar à do tipo". */
  readonly doTipo?: string;
  /** A disciplina que mede o aço do elemento, quando há. */
  readonly disciplina?: string;
  /**
   * A lista é a do elemento nessa disciplina (Fase 42, RN-573) — a do muro ou a do pilar na
   * PILARES DE CONTENÇÃO —, e não a da disciplina inteira. O card diz "a lista do muro".
   */
  readonly porElemento?: true;
}

/**
 * A taxa de aço que a peça segue sem a própria (Fase 41, etapa 2 — RN-543, RN-544; Fase 42, etapa
 * 2 — RN-567, RN-568; etapa 3 — RN-572): a do tipo, quando ele tem — o de bloco no bloco, o de
 * pilar ou de viga na peça do mesmo elemento —; sem ela, a da disciplina que mede o aço do
 * elemento (o serviço de aço do bloco, do baldrame, do radier, do pilar, da viga, da escada, do
 * muro ou do pilar de contenção) — a lista do elemento, quando a disciplina a tem (RN-573). A
 * lista vale inteira, como a conta a lê (`steelRatesFor`).
 */
export function taxaDeBaseDaPeca(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  elemento: PieceElement,
  tipo?: Pick<PileCapType | StructuralElementType, 'name' | 'steelRates'>,
): TaxaDeBase {
  const disciplina = catalog?.disciplines.find((candidata) =>
    candidata.services.some(
      (servico) => servico.measureBy === 'ACO' && serviceElement(servico) === elemento,
    ),
  );
  const daDisciplina = disciplina === undefined ? {} : { disciplina: disciplina.name };
  if (tipo?.steelRates !== undefined && tipo.steelRates.length > 0) {
    return { taxas: tipo.steelRates, doTipo: tipo.name, ...daDisciplina };
  }
  // A lista do elemento, na disciplina que mistura (Fase 42, RN-573); sem ela, a da disciplina.
  const taxas = disciplina === undefined ? undefined : listaDaTaxa(disciplina, elemento);
  const porElemento =
    disciplina !== undefined && temListaPropria(disciplina, elemento)
      ? { porElemento: true as const }
      : {};
  return {
    taxas: taxas === undefined || taxas.length === 0 ? undefined : taxas,
    ...daDisciplina,
    ...porElemento,
  };
}

/**
 * Os serviços aprovados que leem a taxa de aço do elemento (Fase 42, etapa 2 — RN-570, como a
 * RN-547): com um deles, a taxa que a peça lê — a própria, a do tipo, a da disciplina — não muda
 * mais, e o card só a mostra. É o `acoAprovadoDa` da disciplina, restrito ao elemento: o aço da
 * viga aprovado não trava o do pilar. Vazio = a taxa é livre.
 */
export function acoAprovadoDoElemento(
  aprovados: ServicosAprovados | undefined,
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  elemento: PieceElement,
): readonly AprovacaoDoServico[] {
  if (aprovados === undefined || aprovados.size === 0 || catalog === undefined) return [];
  return catalog.disciplines.flatMap((disciplina) =>
    disciplina.services.flatMap((servico) => {
      if (!readsSteelRate(servico) || serviceElement(servico) !== elemento) return [];
      const aprovacao = aprovacaoDe(aprovados, servico.key);
      return aprovacao === undefined ? [] : [aprovacao];
    }),
  );
}

/**
 * A média de hoje do aço do elemento, bitola a bitola (Fase 41, RN-552, RN-554): o que o card do
 * bloco, do baldrame e do radier mostra ao lado de cada kg/m³ — e, desde a Fase 42 (etapa 4,
 * RN-584), o do pilar, da viga, da escada, do muro e do pilar de contenção, com a média da
 * estrutura. Só para ler — a taxa do tipo e a própria não mudam com ela. Sem média, vazio.
 */
export function mediaDoAcoDaPeca(
  media: ApartmentAverageForProject | undefined,
  elemento: PieceElement,
): ReadonlyMap<SteelGauge, MediaAoLado> {
  if (media === undefined) return new Map();
  return new Map(
    STEEL_GAUGES.flatMap(({ code }): [SteelGauge, MediaAoLado][] => {
      const hoje = mediaDaBitola(code, media, elemento);
      return hoje === undefined ? [] : [[code, { rate: hoje.rate, projects: hoje.projects }]];
    }),
  );
}
